import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading5 } from '~/components/atom/Typography';
import RadioButton from '~/components/molecule/RadioButton';
import {
  type Lfa_Conversion_Data_Rejection_Criteria,
  Lfa_Service_Acceptance_Status,
  Lfa_Service_Acceptance_Type,
} from '~/graphql/types';
import RejectBlock from '../../../RejectBlock';
import type { FeedbackType, UpdateFeedbackArgs } from '../../utils/reducer';
import updateLocalState from '../../utils/updateLocalState';
import type { ClearValidationErrorArgs } from '~/components/page/LFA/hooks/useFeedbackValidation';
import type { ValidationError } from '~/components/page/LFA/state/validationState';

const text = {
  Conversion: { rejectionQuestion: 'Waarom is de dienst niet geleverd?' },
  Appointment: { rejectionQuestion: 'Waarom is deze afspraak niet goed?' },
};

type Props = {
  className?: string;
  dataTestId?: string;
  label: string;
  answerOptions: Array<{ label: string; type: Lfa_Service_Acceptance_Status }>;
  serviceFeedback: FeedbackType;
  serviceVersion: number;
  serviceId: string;
  error?: ValidationError;
  rejectionCriteria: Array<Lfa_Conversion_Data_Rejection_Criteria>;
  feedbackType: Lfa_Service_Acceptance_Type;
  clearValidationErrorForField: (args: ClearValidationErrorArgs) => void;
  updateLocalFeedback: (args: UpdateFeedbackArgs) => void;
};

const FeedbackForm: React.FCC<Props> = ({
  serviceFeedback,
  feedbackType,
  clearValidationErrorForField,
  updateLocalFeedback,
  serviceVersion,
  serviceId,
  className,
  dataTestId,
  error,
  rejectionCriteria,
  label,
  answerOptions,
}) => {
  const [selectedOption, setSelectedOption] =
    useState<Lfa_Service_Acceptance_Status>(serviceFeedback.answer);

  const [selectedCriteria, setSelectedCriteria] = useState<
    Array<Lfa_Conversion_Data_Rejection_Criteria>
  >(serviceFeedback.rejectionCriteria);

  const currentCustomReason = useMemo(() => {
    const customReason = serviceFeedback.rejectionCriteria?.find(
      crit => crit.id === 'custom_reason',
    );
    return customReason?.reason ?? '';
  }, [serviceFeedback.rejectionCriteria]);

  const [customReason, setCustomReason] = useState<string>(currentCustomReason);

  // Use refs to track previous values and prevent unnecessary updates
  const prevFeedbackRef = useRef<FeedbackType | undefined>(undefined);
  const isInitializedRef = useRef(false);

  const isRejected = selectedOption === Lfa_Service_Acceptance_Status.Rejected;

  // Update FeedbackForm local state when serviceFeedback prop changes
  useEffect(() => {
    updateLocalState({
      prevFeedbackRef,
      serviceFeedback,
      setSelectedOption,
      setSelectedCriteria,
      setCustomReason,
    });
  }, [serviceFeedback]);

  const handleRejectionCriteriaChange = useCallback(
    (criterion: Lfa_Conversion_Data_Rejection_Criteria) => {
      clearValidationErrorForField({ key: 'Done', field: 'criteria' });
      setSelectedCriteria(prev =>
        prev.some(crit => crit.id === criterion.id)
          ? prev.filter(crit => crit.id !== criterion.id)
          : [...prev, criterion],
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleOptionSelect = useCallback(
    (option: Lfa_Service_Acceptance_Status) => {
      setSelectedOption(option);
      clearValidationErrorForField({
        key: 'Done',
        field: 'criteria',
      });
      clearValidationErrorForField({
        key: 'Done',
        field: 'customReason',
      });

      // Clear rejection criteria when switching away from rejected
      if (option !== Lfa_Service_Acceptance_Status.Rejected) {
        setSelectedCriteria([]);
        setCustomReason('');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Update parent state when this local state changes
  const updateFeedback = useCallback(() => {
    const newFeedback: Partial<FeedbackType> = {
      serviceVersion,
      answer: selectedOption,
      rejectionCriteria: selectedCriteria.map(({ id, reason, label }) => ({
        __typename: 'LFA_Conversion_Data_Rejection_Criteria',
        label,
        id,
        reason: id === 'custom_reason' ? customReason : reason,
      })),
      type: Lfa_Service_Acceptance_Type.Conversion,
    };

    updateLocalFeedback({
      serviceId,
      feedback: newFeedback,
    });
  }, [
    selectedOption,
    selectedCriteria,
    customReason,
    updateLocalFeedback,
    serviceId,
    serviceVersion,
  ]);

  // Update feedback when relevant state changes
  useEffect(() => {
    // Skip the first render to avoid initializing with default values
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }

    updateFeedback();
  }, [updateFeedback]);

  return (
    <JustificationContainer
      direction="column"
      width="100%"
      dataTestId={dataTestId}
      className={className}
      style={{ overflow: 'hidden' }}
    >
      <Heading5 required>{label}</Heading5>

      <JustificationContainer direction="column" gap="xxs" width="100%">
        {answerOptions.map(option => (
          <RadioButton
            key={option.label}
            label={option.label}
            checked={selectedOption === option.type}
            onChange={() => handleOptionSelect(option.type)}
          />
        ))}
      </JustificationContainer>

      {isRejected && (
        <JustificationContainer direction="column" gap="xs" width="90%">
          <RejectBlock
            question={text[feedbackType].rejectionQuestion}
            error={error}
            rejectionCriteria={rejectionCriteria}
            selectedCriteria={selectedCriteria}
            customReason={customReason}
            setCustomReason={reason => {
              setCustomReason(reason);
              clearValidationErrorForField({
                key: 'Done',
                field: 'customReason',
              });
            }}
            handleRejectionCriteriaChange={handleRejectionCriteriaChange}
          />
        </JustificationContainer>
      )}
    </JustificationContainer>
  );
};

export default FeedbackForm;
